/**
 * Builds nodes shaped like a secondary-view `subIssues.nodes[]` entry so `SubIssuesListItem` can
 * render an add or create while the write is in flight. Unknown fields get neutral placeholders
 * until the settle replaces the node. Shared by both overlay producers.
 */
import {escapeHtml} from '@github-ui/issue-create/escapeHtml'
import {TEMP_SUB_ISSUE_ID_PREFIX} from '@github-ui/issue-create/values'
import type {SubIssuesListItemNode} from '../components/sub-issues-list-item-query-types'

// `state` / `stateReason` reach these builders from Relay-typed sources (the issue picker, the
// mutation payload) whose enums are open: the server may send a value this client doesn't know yet.
// They're taken as strings and narrowed to the row's closed unions below, so an unrecognised value
// can't be passed off as a known one.
const ROW_STATE_REASONS = ['COMPLETED', 'DUPLICATE', 'NOT_PLANNED', 'REOPENED'] as const satisfies ReadonlyArray<
  NonNullable<SubIssuesListItemNode['stateReason']>
>

/** An unrecognised state renders as open, matching the `closed` flag derived alongside it. */
export function toRowState(state: string | null | undefined): SubIssuesListItemNode['state'] {
  return state === 'CLOSED' ? 'CLOSED' : 'OPEN'
}

export function toRowStateReason(stateReason: string | null | undefined): SubIssuesListItemNode['stateReason'] {
  return ROW_STATE_REASONS.find(reason => reason === stateReason) ?? null
}

export type SubIssueOptimisticNodeInput = {
  id: string
  title: string
  /** "owner/name"; split into `repository.owner.login` / `repository.name`. */
  nameWithOwner?: string
  number?: number
  titleHTML?: string
  url?: string
  /** Open enums from the Relay-typed picker; narrowed to the row unions by the builder. */
  state?: string
  stateReason?: string | null
  databaseId?: SubIssuesListItemNode['databaseId']
}

export function createTempSubIssueId(): string {
  return `${TEMP_SUB_ISSUE_ID_PREFIX}${crypto.randomUUID()}`
}

export function buildSubIssueOptimisticNode(input: SubIssueOptimisticNodeInput): SubIssuesListItemNode {
  const [ownerLogin = '', repoName = ''] = (input.nameWithOwner ?? '/').split('/')
  const state = toRowState(input.state)
  return {
    id: input.id,
    closed: state === 'CLOSED',
    state,
    stateReason: toRowStateReason(input.stateReason),
    issueDependenciesSummary: {blockedBy: 0},
    assignees: {totalCount: 0, edges: []},
    url: input.url ?? '',
    repository: {name: repoName, owner: {login: ownerLogin}},
    databaseId: input.databaseId ?? null,
    number: input.number ?? 0,
    title: input.title,
    // Escape rather than render markdown: the settle would otherwise visibly rewrite the row.
    titleHTML: input.titleHTML ?? escapeHtml(input.title),
    issueType: null,
    subIssuesSummary: {total: 0, completed: 0},
    closedByPullRequestsReferences: {totalCount: 0},
  }
}

/** Required keys stay required; every other key is optional and may come back null. */
type ServerResponse<T, Required extends keyof T> = Pick<T, Required> & {
  [K in Exclude<keyof T, Required>]?: T[K] | null
}

/**
 * What the settle fold reads off a server-confirmed sub-issue, derived from the row type so it
 * tracks the generated query rather than restating it. `state`, `stateReason` and `issueType.color`
 * are re-widened because the responses type them as open enums, where the row's are closed unions.
 */
export type ServerSubIssueNodeInput = Omit<
  ServerResponse<SubIssuesListItemNode, 'id' | 'number' | 'title' | 'url' | 'repository'>,
  'closed' | 'state' | 'stateReason' | 'issueType'
> & {
  state?: string | null
  stateReason?: string | null
  issueType?: {id: string; name: string; color: string} | null
}

/** Maps a server-confirmed sub-issue (add or create response) to the row shape, for the settle fold. */
export function buildSubIssueNodeFromServer(input: ServerSubIssueNodeInput): SubIssuesListItemNode {
  const state = toRowState(input.state)
  return {
    id: input.id,
    closed: state === 'CLOSED',
    state,
    stateReason: toRowStateReason(input.stateReason),
    issueDependenciesSummary: {blockedBy: input.issueDependenciesSummary?.blockedBy ?? 0},
    assignees: {totalCount: input.assignees?.totalCount ?? 0, edges: input.assignees?.edges ?? []},
    url: input.url,
    repository: {name: input.repository.name, owner: {login: input.repository.owner.login}},
    databaseId: input.databaseId ?? null,
    number: input.number,
    title: input.title,
    titleHTML: input.titleHTML ?? escapeHtml(input.title),
    // `color` is a presentational open enum on the Relay-typed responses feeding this builder; the
    // row type's closed union omits the placeholder member, so cast rather than restate all colours.
    issueType: (input.issueType ?? null) as SubIssuesListItemNode['issueType'],
    subIssuesSummary: input.subIssuesSummary ?? {total: 0, completed: 0},
    closedByPullRequestsReferences: {totalCount: input.closedByPullRequestsReferences?.totalCount ?? 0},
  }
}
